import {Component, OnInit} from '@angular/core';
import {ActivatedRoute, NavigationEnd, NavigationExtras, Router} from "@angular/router";
import {Website} from "../../website";
import {Store} from "@ngrx/store";
import * as fromStore from '../../../../store';
import {CloudflareService} from "../services/cloudflare.service";
import {filter, map, switchMap, takeUntil} from "rxjs/operators";
import {UserSubscription, UserSubscriptionCategory} from "../../../../user/user-subscription";
import {Subject} from "rxjs";
import tabs from "./cdn-tabs.json";
import {GoogleAnalyticsService} from "../../../../shared/services/google-analytics/google-analytics.service";
import {TypeUtils} from "../../../../shared/utils/type-utils";
import {combineLatest} from "rxjs";
import {User} from "../../../../user/user";
import {Location} from "@angular/common";
import {Workspace} from "../../../../user-managment/workspace";
import {SwitchPlanComponent} from "../../../../components/common/switch-plan/switch-plan.component";
import {UserSubscriptionStatus} from "../../../../user/user-subscription-status";
import {DbModalService} from "../../../../modules/modal/modal";
import {Utils} from "../../../../shared/utils/utils";

interface ITab {
  id: 'overview' | 'full-page' | 'settings' | string;
  label: string;
}

@Component({
    selector: 'db-cdn',
    templateUrl: './cdn.component.html',
    styleUrls: ['./cdn.component.scss'],
    standalone: false
})
export class CDNComponent implements OnInit {

  public website: Website;
  public currentSubscription: UserSubscription;
  public annualSubscription: UserSubscription;
  public userSubscriptionStatus = UserSubscriptionStatus;
  public showSwitchPlanPopUp: boolean = false;
  public user: User;
  public currentWorkspace: Workspace;
  demoUser: boolean = false;

  public tabs: ITab[] = tabs;
  public activeTab: ITab['id'] = tabs[0].id;

  private _destroyStream$: Subject<boolean> = new Subject<boolean>();
  public isStatusPending: boolean = true;

  public isNameServer: boolean;
  public server: string;
  public hasCertificate: boolean = true;
  public hasDcvRecord: boolean = false;
  public serverIp: any;
  public enabledCdnCount: any;

  constructor(
    private _route: ActivatedRoute,
    private _cloudflareService: CloudflareService,
    private _store: Store<fromStore.DbStoreState>,
    private _googleAnalyticsService: GoogleAnalyticsService,
    private _router: Router,
    private _dbModalService: DbModalService,
    private readonly location: Location,
  ) {
  }

  ngOnInit() {
    this.clearQueryParams();
    this._router.events
      .pipe(
        takeUntil(this._destroyStream$),
        filter(event => event instanceof NavigationEnd)
      )
      .subscribe(() => {
        const queryParams: any = (<any>this._route.queryParams).value;
        queryParams.active_tab ? this.activeTab = 'settings' : this.activeTab = tabs[0].id;

        const queryParamsIndex: number = this._router.url.indexOf('?');
        if (queryParamsIndex !== -1) {
          this.location.replaceState(this._router.url.substring(0, queryParamsIndex));
        }
      });
    this._initData();
    this._getCFData();
    this._getUserAndSubscriptionData();
  }

  private _initData(): void {
    this.website = this._route.snapshot.parent.parent.data['website'];
    if (this.website) {
      this._getWebsite();
      this._getCFDefaultDnsRecord();
    }
  }

  public clearQueryParams(): void {
    const navigationExtras: NavigationExtras = {
      queryParamsHandling: 'merge',
      queryParams: null
    };
    this._router.navigate([], navigationExtras);
  }

  private _getWebsite(): void {
    this._store.select(fromStore.getWebsitesLoaded)
      .pipe(
        filter((loaded: boolean) => loaded),
        switchMap(() => combineLatest(
          this._store.select(fromStore.getWebsites),
          this._store.select(fromStore.getWebsite(this.website?.id))
        )),
        takeUntil(this._destroyStream$)
      )
      .subscribe(([websites, website]) => {
        this.website = website;
        this.enabledCdnCount = websites.filter((_website: Website) => !!_website.cloudflareEnabled);
      });
  }

  private _getCFData(): void {
    this._store.select(fromStore.getCloudflareData(this.website?.id))
      .pipe(
        filter(({cfStatus, hasDcvRecord}) => !!cfStatus && hasDcvRecord !== undefined),
        map(({cfStatus, hasDcvRecord}) => ({
          status: cfStatus === 'pending', hasDcvRecord
        }))
      )
      .subscribe(({status, hasDcvRecord}) => {
        this.isStatusPending = status;
        this.hasDcvRecord = hasDcvRecord;
      });
  }

  private _getCFDefaultDnsRecord(): void {
    this._store.select(fromStore.getCloudflareDefaultRecord(this.website?.id))
      .pipe(
        takeUntil(this._destroyStream$))
      .subscribe(data => {
        if (!TypeUtils.isNull(data) && !TypeUtils.isEmpty(data)) {
          this.isNameServer = !!data.tenwebNs;
          this.server = data.server;
          this.hasCertificate = !!data.hasCertificate;
          this.serverIp = data.dnsRecords && data.dnsRecords[0];
        }
      });
  }


  public setActiveTab(tabId: string): void {
    this.activeTab = tabId as ITab['id'];
  }

  private _getUserAndSubscriptionData(): void {
    this._store.select(fromStore.getUserLoaded)
      .pipe(
        filter((loaded: boolean) => loaded),
        switchMap(() => combineLatest(
            this._store.select(fromStore.getUser),
            this._store.select(fromStore.getUserSubscription),
            this._store.select(fromStore.getCurrentWorkspace),
            this._store.select(fromStore.getUserAvailableHostingSubscriptions('platform')),
          )
        ),
        takeUntil(this._destroyStream$)
      )
      .subscribe(([user, userSubscription, currentWorkspace, availableSubscriptions]) => {
        this.user = user;
        this.currentSubscription = userSubscription;
        this.currentWorkspace = currentWorkspace;
        this.demoUser = Utils.isDemoUser(userSubscription, user);
        const category = this.currentSubscription && this.currentSubscription.isBusiness ? UserSubscriptionCategory.ENTERPRISE : UserSubscriptionCategory.PREMIUM;
        this.annualSubscription = availableSubscriptions.find(userSubscription =>
          userSubscription && userSubscription.category && userSubscription.category === category
          && userSubscription.tabCategory && userSubscription.tabCategory === this.currentSubscription?.tabCategory
          && userSubscription.plan && userSubscription.plan.periodNumber && userSubscription.plan.periodNumber === 12
        );
/*        this._showSwitchToAnnualDialog();*/
      });
  }

  private _showSwitchToAnnualDialog(): void {
    if (this.user && this.currentSubscription && this.currentSubscription.category === UserSubscriptionCategory.PERSONAL
      && !this.currentSubscription.oldPersonalPlatform
      && this.currentSubscription
      && (this.currentSubscription.isBusiness || this.currentSubscription.isEcommerce)
      && this.currentSubscription.periodNumber !== 12
      && this.currentSubscription.plan && !this.currentSubscription.plan.additionalDomainCount
      && this.currentSubscription.plan && this.currentSubscription.plan.price === this.currentSubscription.plan.planPrice
      && this.currentSubscription.plan && !this.currentSubscription.plan.additionalDomainCount
      && this.annualSubscription
      && !this._dbModalService.hasOpenModals()
      && ![this.userSubscriptionStatus.canceled, this.userSubscriptionStatus.failed, this.userSubscriptionStatus.expired].includes(this.currentSubscription.status)
      && this._shouldShowPopup()
      && (!this.currentSubscription.couponCode || (this.currentSubscription.couponCode && this.currentSubscription.couponCode === '1wf'))
      && !this.showSwitchPlanPopUp) {
      this.showSwitchPlanPopUp = true;
      const modal = this._dbModalService.open(SwitchPlanComponent);
      modal.componentInstance.userSubscription = this.currentSubscription;
      modal.componentInstance.demoUser = this.demoUser;
      modal.componentInstance.annualSubscription = this.annualSubscription;
    }
  }

  private _shouldShowPopup(): boolean {
    // check if it's the user's first visit to the dashboard
    if (!localStorage.getItem('switchPlanFirstVisitCdn' + this.user.id)) {
      // Set the first visit timestamp
      localStorage.setItem('switchPlanFirstVisitCdn' + this.user.id, Date.now().toString());
      return true;
    }
    return false;
  }
}
